import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  ArrowRightLeft,
  RotateCcw,
  DollarSign,
  Newspaper,
  Package,
  Trash2,
  Settings,
  Bell,
  BarChart3,
  LogOut,
  Sun,
  Moon,
  User,
  UserCog,
} from "lucide-react";
import {
  Avatar as NxAvatar,
  BrandMark,
  Button as NxButton,
  IconRail,
  TopBar,
} from "@nxivo/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

function icon(node: ReactNode) {
  return <span className="flex h-5 w-5 items-center justify-center [&_svg]:h-[18px] [&_svg]:w-[18px]">{node}</span>;
}

const PAGE_TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/issue": "Issue Register",
  "/return": "Return Register",
  "/fines": "Fine Register",
  "/membership": "Student Register",
  "/accession": "Accession Register",
  "/periodicals": "Periodicals",
  "/stock": "Stock Register",
  "/weeding": "Weeding Register",
  "/reports": "Reports",
  "/notifications": "Notifications",
  "/settings": "Settings",
  "/users": "User Management",
};

function titleForPath(pathname: string): string {
  if (pathname.startsWith("/masters/")) {
    const master = pathname.split("/")[2] || "Masters";
    return `Masters · ${master.charAt(0).toUpperCase()}${master.slice(1)}`;
  }
  return PAGE_TITLES[pathname] || "Bindley";
}

function activeIdForPath(pathname: string): string {
  if (pathname === "/") return "dashboard";
  if (pathname.startsWith("/masters") || pathname === "/settings" || pathname === "/users") return "settings";
  const first = pathname.split("/").filter(Boolean)[0];
  return first || "dashboard";
}

export function DashboardShell() {
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [themeMode, setThemeMode] = useState<"light" | "dark">("dark");
  const { user, logout, api, refreshMe } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const activeId = activeIdForPath(location.pathname);
  const title = titleForPath(location.pathname);

  useEffect(() => {
    const stored = localStorage.getItem("lms_theme");
    const next = stored === "light" ? "light" : "dark";
    setThemeMode(next);
    document.documentElement.classList.toggle("dark", next === "dark");
    document.documentElement.setAttribute("data-color-mode", next);
  }, []);

  useEffect(() => {
    setProfileName(user?.full_name || "");
  }, [user?.full_name]);

  const handleToggleTheme = () => {
    const nextTheme = themeMode === "dark" ? "light" : "dark";
    setThemeMode(nextTheme);
    localStorage.setItem("lms_theme", nextTheme);
    document.documentElement.classList.toggle("dark", nextTheme === "dark");
    document.documentElement.setAttribute("data-color-mode", nextTheme);
  };

  const openProfileDialog = () => {
    setProfileName(user?.full_name || "");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setProfileDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    const trimmedName = profileName.trim();
    if (!trimmedName) {
      toast.error("Name is required");
      return;
    }

    const hasPasswordInput = Boolean(currentPassword || newPassword || confirmPassword);
    if (hasPasswordInput) {
      if (!currentPassword || !newPassword || !confirmPassword) {
        toast.error("Fill all password fields to change password");
        return;
      }
      if (newPassword.length < 6) {
        toast.error("New password must be at least 6 characters");
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error("New password and confirm password must match");
        return;
      }
    }

    try {
      setIsSavingProfile(true);
      if (trimmedName !== (user?.full_name || "")) {
        await api.patch("/auth/me", { full_name: trimmedName });
      }
      if (hasPasswordInput) {
        await api.patch("/auth/me/password", { currentPassword, newPassword });
      }
      await refreshMe();
      setProfileDialogOpen(false);
      toast.success(hasPasswordInput ? "Profile and password updated" : "Profile updated");
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Failed to update profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const railItems = useMemo(() => {
    const items = [
      { id: "dashboard", label: "Dashboard", icon: icon(<LayoutDashboard strokeWidth={1.75} />) },
      { id: "issue", label: "Issue", icon: icon(<ArrowRightLeft strokeWidth={1.75} />) },
      { id: "return", label: "Return", icon: icon(<RotateCcw strokeWidth={1.75} />) },
      { id: "fines", label: "Fines", icon: icon(<DollarSign strokeWidth={1.75} />) },
      { id: "membership", label: "Students", icon: icon(<Users strokeWidth={1.75} />) },
      { id: "accession", label: "Accession", icon: icon(<BookOpen strokeWidth={1.75} />) },
      { id: "periodicals", label: "Periodicals", icon: icon(<Newspaper strokeWidth={1.75} />) },
      { id: "stock", label: "Stock", icon: icon(<Package strokeWidth={1.75} />) },
      { id: "weeding", label: "Weeding", icon: icon(<Trash2 strokeWidth={1.75} />) },
      { id: "reports", label: "Reports", icon: icon(<BarChart3 strokeWidth={1.75} />) },
    ];
    return items;
  }, []);

  const bottomItems = useMemo(() => {
    const items = [
      { id: "notifications", label: "Notifications", icon: icon(<Bell strokeWidth={1.75} />) },
      { id: "settings", label: "Settings", icon: icon(<Settings strokeWidth={1.75} />) },
    ];
    if (user?.role === "admin") {
      items.splice(1, 0, { id: "users", label: "Users", icon: icon(<UserCog strokeWidth={1.75} />) });
    }
    return items;
  }, [user?.role]);

  const onNavigate = (id: string) => {
    if (id === "dashboard") navigate("/");
    else navigate(`/${id}`);
  };

  return (
    <div className="flex min-h-[100dvh] overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <div className="hidden lg:flex">
        <IconRail
          activeId={activeId}
          logo={<BrandMark size={40} product="bindley" title="Bindley" />}
          onNavigate={onNavigate}
          items={railItems}
          bottomItems={bottomItems}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar
          title={title}
          subtitle="Bindley · Library management"
          right={
            <div className="flex items-center gap-2">
              <NxButton variant="ghost" size="sm" onClick={handleToggleTheme} aria-label="Toggle theme">
                {themeMode === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </NxButton>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="rounded-full outline-none" data-testid="topbar-profile-menu">
                    <NxAvatar name={user?.full_name || user?.email || "User"} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="truncate text-sm font-medium leading-none">{user?.full_name || "User"}</p>
                    <p className="mt-1 truncate text-xs text-muted-foreground">{user?.email || ""}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openProfileDialog}>
                    <User className="mr-2 h-4 w-4" />
                    Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleToggleTheme}>
                    {themeMode === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                    {themeMode === "dark" ? "Light Mode" : "Dark Mode"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        />

        {/* Mobile icon strip */}
        <div className="flex gap-1 overflow-x-auto border-b border-[var(--border-subtle)] bg-[var(--bg)] px-2 py-2 lg:hidden">
          {[...railItems, ...bottomItems].map((item) => (
            <button
              key={item.id}
              type="button"
              title={item.label}
              onClick={() => onNavigate(item.id)}
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                activeId === item.id ? "bg-[var(--accent)]/20 text-[var(--accent)]" : "text-[var(--text-muted)]"
              }`}
            >
              {item.icon}
            </button>
          ))}
        </div>

        <main className="flex-1 overflow-auto">
          <div className="animate-fade-in p-4 pb-10 lg:p-6 lg:pb-12">
            <Outlet />
          </div>
        </main>
      </div>

      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>Update your name and optionally change your password.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="profile-name">Name</Label>
              <Input id="profile-name" value={profileName} onChange={(e) => setProfileName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="profile-email">Email</Label>
              <Input id="profile-email" value={user?.email || ""} disabled />
            </div>
            <Separator />
            <div className="grid gap-1.5">
              <Label htmlFor="current-password">Current Password</Label>
              <Input id="current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-password">New Password</Label>
              <Input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialogOpen(false)} disabled={isSavingProfile}>
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
              {isSavingProfile ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
